import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogOut, Users, CheckCircle, Clock, Ban, AlertTriangle, Sparkles, Search, MessageCircle, KeyRound, Save, Settings as SettingsIcon, RotateCcw } from "lucide-react";
import { format } from "date-fns";

const STATUS_LABEL = { em_teste: "Em teste", ativo: "Ativo", vencido: "Vencido", bloqueado: "Bloqueado" };
const STATUS_COLOR = {
  em_teste: "bg-amber-50 text-amber-700",
  ativo: "bg-emerald-50 text-emerald-700",
  vencido: "bg-red-50 text-red-700",
  bloqueado: "bg-zinc-200 text-zinc-700",
};
const PLANO_LABEL = { teste: "Teste", mensal: "Mensal", anual: "Anual" };
const safeDate = (s) => { try { return s ? format(new Date(s), "dd/MM/yyyy") : "—"; } catch { return "—"; } };
const safeDateTime = (s) => { try { return s ? format(new Date(s), "dd/MM/yy HH:mm") : "—"; } catch { return "—"; } };
const formatBRL = (v) => (v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const waUrl = (phone, nome) => phone ? `https://wa.me/${phone.replace(/\D/g,"")}?text=${encodeURIComponent(`Olá ${nome||""}`)}` : null;

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({});
  const [saloes, setSaloes] = useState([]);
  const [query, setQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/saloes${query ? `?q=${encodeURIComponent(query)}` : ""}${filterStatus !== "all" ? `${query?"&":"?"}status=${filterStatus}` : ""}`),
      ]);
      setStats(s.data); setSaloes(l.data);
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  }, [query, filterStatus]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const openSalao = async (id) => {
    try {
      const { data } = await api.get(`/admin/saloes/${id}`);
      setSelected(data);
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white border border-[#e4e4e7] rounded-lg p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${color}`}><Icon className="h-4 w-4" /></div>
      <div><p className="text-[10px] text-[#71717a] uppercase tracking-wider">{label}</p><p className="text-xl font-bold text-[#18181b] tabular-nums">{value ?? 0}</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f7f6f4] text-[#18181b]" data-testid="admin-page">
      <header className="bg-white border-b border-[#e4e4e7] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/connect-icon.svg" alt="" className="h-7 w-7" />
          <div>
            <p className="text-[10px] font-semibold tracking-widest salonup-logo-badge leading-none">SalonUp Admin</p>
            <h1 className="text-lg font-bold tracking-tight text-[#18181b]">CONNECT · Painel</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#71717a] mr-2">{user?.email}</span>
          <Button data-testid="admin-settings-btn" onClick={() => setShowSettings(true)} size="sm" className="bg-white border border-[#e4e4e7] text-[#18181b] hover:bg-[#f0efed] h-8 text-xs"><SettingsIcon className="h-3.5 w-3.5 mr-1" /> Config.</Button>
          <Button data-testid="admin-logout-btn" onClick={logout} size="sm" className="bg-white border border-[#e4e4e7] text-[#18181b] hover:bg-[#f0efed] h-8 text-xs"><LogOut className="h-3.5 w-3.5 mr-1" /> Sair</Button>
        </div>
      </header>

      <main className="p-6 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="admin-stats">
          <Stat icon={Users} label="Total" value={stats.total} color="bg-zinc-100 text-zinc-700" />
          <Stat icon={Clock} label="Em teste" value={stats.em_teste} color="bg-amber-50 text-amber-700" />
          <Stat icon={CheckCircle} label="Ativos" value={stats.ativos} color="bg-emerald-50 text-emerald-700" />
          <Stat icon={AlertTriangle} label="Vencidos" value={stats.vencidos} color="bg-red-50 text-red-700" />
          <Stat icon={Ban} label="Bloqueados" value={stats.bloqueados} color="bg-zinc-200 text-zinc-700" />
          <Stat icon={AlertTriangle} label="Vence em 7d" value={stats.vencendo_7d} color="bg-amber-50 text-amber-700" />
          <Stat icon={Sparkles} label="Novos 7d" value={stats.novos_7d} color="bg-emerald-50 text-emerald-700" />
        </div>

        <div className="bg-white border border-[#e4e4e7] rounded-lg">
          <div className="p-4 flex items-center gap-3 border-b border-[#e4e4e7]">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#a1a1aa]" />
              <Input data-testid="admin-search" placeholder="Buscar salão/e-mail..." value={query} onChange={(e)=>setQuery(e.target.value)} className="pl-9 h-9 text-sm" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="admin-filter-status" className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos status</SelectItem>
                {Object.entries(STATUS_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-saloes-table">
              <thead className="bg-[#f7f6f4] text-[10px] text-[#71717a] uppercase tracking-wider">
                <tr><th className="text-left px-4 py-2">Salão</th><th className="text-left px-4 py-2">Responsável</th><th className="text-left px-4 py-2">E-mail</th><th className="text-left px-4 py-2">WhatsApp</th><th className="text-left px-4 py-2">Cadastro</th><th className="text-left px-4 py-2">Último acesso</th><th className="text-right px-4 py-2">Camp.</th><th className="text-right px-4 py-2">Contatos</th><th className="text-left px-4 py-2">Plano</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Vence em</th></tr>
              </thead>
              <tbody>
                {saloes.map((s) => (
                  <tr key={s.id} onClick={()=>openSalao(s.id)} data-testid={`admin-salao-${s.id}`} className="border-t border-[#f4f4f5] hover:bg-[#f7f6f4] cursor-pointer text-sm">
                    <td className="px-4 py-2 font-medium text-[#18181b]">{s.nome}</td>
                    <td className="px-4 py-2 text-[#71717a]">{s.responsavel_nome || "—"}</td>
                    <td className="px-4 py-2 text-[#71717a]">{s.email}</td>
                    <td className="px-4 py-2">{s.whatsapp ? <a onClick={(e)=>e.stopPropagation()} href={waUrl(s.whatsapp, s.responsavel_nome)} target="_blank" rel="noreferrer" className="text-[#1b4332] hover:underline inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" />{s.whatsapp}</a> : <span className="text-[#a1a1aa]">—</span>}</td>
                    <td className="px-4 py-2 text-[#71717a] text-xs">{safeDate(s.created_at)}</td>
                    <td className="px-4 py-2 text-[#71717a] text-xs">{safeDateTime(s.ultimo_acesso)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{s.pipelines_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{s.contacts_count}</td>
                    <td className="px-4 py-2 text-[#71717a]">{PLANO_LABEL[s.plano] || "—"}</td>
                    <td className="px-4 py-2"><span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_COLOR[s.status] || "bg-zinc-100 text-zinc-700"}`}>{STATUS_LABEL[s.status] || "—"}</span></td>
                    <td className="px-4 py-2 text-xs text-[#71717a]">{safeDate(s.data_vencimento)}</td>
                  </tr>
                ))}
                {saloes.length === 0 && <tr><td colSpan={11} className="text-center py-10 text-[#a1a1aa] text-sm">Nenhum salão</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <SalaoDrawer salao={selected} onClose={() => { setSelected(null); load(); }} />
      <AdminSettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
}

function SalaoDrawer({ salao, onClose }) {
  const [edit, setEdit] = useState({ status: "", plano: "", data_vencimento: "", admin_notes: "" });
  const [compra, setCompra] = useState({ data: format(new Date(), "yyyy-MM-dd"), valor: "", forma: "pix", referencia: "", observacao: "" });
  const [resetResult, setResetResult] = useState(null);
  const [excluidas, setExcluidas] = useState([]);

  const loadExcluidas = useCallback(async (sid) => {
    try { const { data } = await api.get(`/admin/saloes/${sid}/campanhas-excluidas`); setExcluidas(data); }
    catch { setExcluidas([]); }
  }, []);

  useEffect(() => { if (salao) loadExcluidas(salao.id); }, [salao, loadExcluidas]);

  useEffect(() => {
    if (salao) {
      setEdit({ status: salao.status || "em_teste", plano: salao.plano || "teste", data_vencimento: salao.data_vencimento || "", admin_notes: salao.admin_notes || "" });
      setResetResult(null);
    }
  }, [salao]);

  if (!salao) return null;

  const saveFields = async () => {
    try {
      await api.patch(`/admin/saloes/${salao.id}`, edit);
      toast.success("Salvo");
      onClose();
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const addCompra = async () => {
    if (!compra.data || !compra.valor) return toast.error("Preencha data e valor");
    try {
      await api.post(`/admin/saloes/${salao.id}/compras`, { ...compra, valor: parseFloat(compra.valor) });
      toast.success("Compra registrada");
      setCompra({ data: format(new Date(), "yyyy-MM-dd"), valor: "", forma: "pix", referencia: "", observacao: "" });
      const { data } = await api.get(`/admin/saloes/${salao.id}`);
      Object.assign(salao, data);
      setEdit({ ...edit });
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const restaurar = async (c) => {
    if (!window.confirm(`Restaurar a campanha "${c.nome}" com ${c.leads} leads?`)) return;
    try {
      const { data } = await api.post(`/admin/saloes/${salao.id}/campanhas/${c.id}/restaurar`);
      toast.success(`Campanha restaurada (${data.leads_restaurados} leads)`);
      loadExcluidas(salao.id);
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const resetPwd = async () => {
    if (!window.confirm("Gerar nova senha? O salão será obrigado a trocar no próximo login.")) return;
    try {
      const { data } = await api.post(`/admin/saloes/${salao.id}/reset-password`);
      setResetResult(data.temp_password);
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  return (
    <Dialog open={!!salao} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-salao-drawer">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">{salao.nome} <span className="text-xs text-[#71717a] font-normal">— {salao.email}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="bg-[#f7f6f4] rounded p-2"><p className="text-[10px] text-[#71717a]">Campanhas</p><p className="text-lg font-bold tabular-nums">{salao.pipelines_count}</p></div>
            <div className="bg-[#f7f6f4] rounded p-2"><p className="text-[10px] text-[#71717a]">Contatos</p><p className="text-lg font-bold tabular-nums">{salao.contacts_count}</p></div>
            <div className="bg-[#f7f6f4] rounded p-2"><p className="text-[10px] text-[#71717a]">Cadastro</p><p className="text-sm font-semibold">{safeDate(salao.created_at)}</p></div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Status</Label>
              <Select value={edit.status} onValueChange={(v)=>setEdit({...edit,status:v})}>
                <SelectTrigger data-testid="admin-edit-status" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS_LABEL).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Plano</Label>
              <Select value={edit.plano} onValueChange={(v)=>setEdit({...edit,plano:v})}>
                <SelectTrigger data-testid="admin-edit-plano" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PLANO_LABEL).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Vence em</Label>
              <Input data-testid="admin-edit-vencimento" type="date" value={edit.data_vencimento || ""} onChange={(e)=>setEdit({...edit, data_vencimento: e.target.value})} className="h-8 text-xs" /></div>
          </div>
          <div>
            <Label className="text-xs">Observações internas</Label>
            <Input data-testid="admin-edit-notes" value={edit.admin_notes} onChange={(e)=>setEdit({...edit, admin_notes: e.target.value})} className="h-8 text-xs" placeholder="Notas visíveis só para admin"/>
          </div>
          <Button data-testid="admin-save-btn" onClick={saveFields} className="bg-[#1b4332] hover:bg-[#143326] text-white h-8 text-xs font-semibold"><Save className="h-3.5 w-3.5 mr-1" /> Salvar</Button>

          <div className="border-t pt-4 border-[#e4e4e7]">
            <p className="text-xs font-semibold text-[#18181b] mb-2">Registrar compra manual</p>
            <div className="grid grid-cols-5 gap-2">
              <Input type="date" value={compra.data} onChange={(e)=>setCompra({...compra,data:e.target.value})} className="h-8 text-xs" data-testid="compra-data" />
              <Input placeholder="Valor" type="number" step="0.01" value={compra.valor} onChange={(e)=>setCompra({...compra,valor:e.target.value})} className="h-8 text-xs" data-testid="compra-valor" />
              <Select value={compra.forma} onValueChange={(v)=>setCompra({...compra,forma:v})}>
                <SelectTrigger data-testid="compra-forma" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pix">Pix</SelectItem><SelectItem value="cartao">Cartão</SelectItem><SelectItem value="boleto">Boleto</SelectItem><SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Ref." value={compra.referencia} onChange={(e)=>setCompra({...compra,referencia:e.target.value})} className="h-8 text-xs" data-testid="compra-ref" />
              <Button onClick={addCompra} className="bg-[#1b4332] hover:bg-[#143326] text-white h-8 text-xs" data-testid="compra-add">Registrar</Button>
            </div>
            <Input placeholder="Observação (opcional)" value={compra.observacao} onChange={(e)=>setCompra({...compra,observacao:e.target.value})} className="h-8 text-xs mt-2" data-testid="compra-obs" />
            {salao.compras?.length > 0 && (
              <div className="mt-3 bg-[#f7f6f4] rounded p-2 max-h-32 overflow-y-auto">
                {salao.compras.map(c => (
                  <div key={c.id} className="text-xs text-[#71717a] py-1 border-b border-[#f4f4f5] last:border-0 flex justify-between">
                    <span>{safeDate(c.data)} · {c.forma} · {c.referencia || "-"}</span>
                    <span className="font-semibold text-[#18181b]">{formatBRL(c.valor)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t pt-4 border-[#e4e4e7]">
            <Button onClick={resetPwd} data-testid="admin-reset-pwd" className="bg-amber-50 text-amber-700 hover:bg-amber-100 h-8 text-xs"><KeyRound className="h-3.5 w-3.5 mr-1" /> Gerar nova senha</Button>
            {resetResult && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900" data-testid="reset-result">
                Senha temporária: <strong className="font-mono bg-white px-2 py-0.5 rounded">{resetResult}</strong>
                <p className="text-[10px] text-amber-700 mt-1">Copie agora — só é mostrada uma vez. O salão será obrigado a trocar.</p>
              </div>
            )}
          </div>

          <div className="border-t pt-4 border-[#e4e4e7]" data-testid="admin-campanhas-excluidas">
            <p className="text-xs font-semibold mb-2">Campanhas excluídas</p>
            {excluidas.length === 0 ? (
              <p className="text-xs text-[#71717a]" data-testid="admin-excluidas-vazio">Nenhuma campanha na lixeira.</p>
            ) : (
              <div className="bg-[#f7f6f4] rounded p-2 space-y-1">
                {excluidas.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-xs py-1 border-b border-[#ecebe8] last:border-0" data-testid={`excluida-${c.id}`}>
                    <span><strong className="text-[#18181b]">{c.nome}</strong> · {c.leads} leads · excluída em {safeDateTime(c.deleted_at)}</span>
                    <Button onClick={() => restaurar(c)} data-testid={`restaurar-${c.id}`} className="bg-[#1b4332] hover:bg-[#143326] text-white h-7 text-xs"><RotateCcw className="h-3 w-3 mr-1" /> Restaurar</Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {salao.logs?.length > 0 && (
            <div className="border-t pt-4 border-[#e4e4e7]">
              <p className="text-xs font-semibold mb-2">Histórico de ações</p>
              <div className="bg-[#f7f6f4] rounded p-2 max-h-32 overflow-y-auto space-y-1">
                {salao.logs.slice(0, 20).map(l => (
                  <div key={l.id} className="text-[11px] text-[#71717a]">
                    <span className="text-[#18181b]">{safeDateTime(l.created_at)}</span> · {l.admin_email} · <strong>{l.action}</strong>{l.after && Object.keys(l.after).length ? ` → ${JSON.stringify(l.after)}` : ""}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdminSettingsModal({ open, onClose }) {
  const [cfg, setCfg] = useState({ trial_days: 30, support_whatsapp: "" });
  useEffect(() => {
    if (open) api.get("/admin/settings").then(({data}) => setCfg(data)).catch(() => {});
  }, [open]);
  const save = async () => {
    try {
      await api.put("/admin/settings", { trial_days: Number(cfg.trial_days), support_whatsapp: cfg.support_whatsapp });
      toast.success("Config salva"); onClose();
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="admin-settings-modal">
        <DialogHeader><DialogTitle className="text-base">Configurações da plataforma</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Dias do período de teste</Label>
            <Input data-testid="cfg-trial" type="number" min="1" max="365" value={cfg.trial_days} onChange={(e)=>setCfg({...cfg,trial_days:e.target.value})} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">WhatsApp de suporte (ex: 5511999999999)</Label>
            <Input data-testid="cfg-wa" value={cfg.support_whatsapp} onChange={(e)=>setCfg({...cfg,support_whatsapp:e.target.value})} className="h-8 text-xs" /></div>
          <Button onClick={save} data-testid="cfg-save" className="w-full bg-[#1b4332] hover:bg-[#143326] text-white h-8 text-xs font-semibold">Salvar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
